import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { AuthContext } from '../App';
import Chat from '../components/Chat';
import ReportDialog from '../components/ReportDialog';

const TIP_PRESETS = [100, 200, 500, 1000, 2000, 5000];

function formatDuration(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function ProfilePage() {
  const { username } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [activeTab, setActiveTab] = useState('streams');
  const [clips, setClips] = useState([]);
  const [clipsLoaded, setClipsLoaded] = useState(false);
  const [panels, setPanels] = useState([]);

  const [tipAmount, setTipAmount] = useState(500);
  const [tipMsg, setTipMsg] = useState('');
  const [tipName, setTipName] = useState('');
  const [tipping, setTipping] = useState(false);
  const [tipStatus, setTipStatus] = useState('');
  const [subStatus, setSubStatus] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.profile(username)
      .then(d => {
        setProfile(d);
        setFollowing(d.is_following);
        setTipName(user?.display_name || user?.username || '');
      })
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
    api.panels(username).then(setPanels).catch(() => {});
  }, [username]);

  const handleFollow = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      if (following) { await api.unfollow(username); setFollowing(false); }
      else { await api.follow(username); setFollowing(true); }
      setProfile(p => ({ ...p, follower_count: p.follower_count + (following ? -1 : 1) }));
    } catch (e) {}
  };

  const handleTip = async () => {
    setTipping(true); setTipStatus('');
    try {
      await api.tip(username, { amount_cents: tipAmount, message: tipMsg, tipper_name: tipName || 'Anonymous' });
      setTipStatus(`✅ Tip of $${(tipAmount / 100).toFixed(2)} sent!`);
    } catch (e) { setTipStatus(`❌ ${e.message}`); }
    finally { setTipping(false); }
  };

  const handleSubscribe = async () => {
    if (!user) { navigate('/login'); return; }
    setSubscribing(true); setSubStatus('');
    try {
      await api.subscribe(username, { amount_cents: 499 });
      setSubStatus('✅ Subscribed for $4.99/month!');
    } catch (e) { setSubStatus(`❌ ${e.message}`); }
    finally { setSubscribing(false); }
  };

  if (loading) return <div className="loading-screen">Loading...</div>;
  if (!profile) return null;

  const { user: streamer, follower_count, is_live, past_streams } = profile;

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'clips' && !clipsLoaded) {
      api.channelClips(username).then(setClips).catch(() => {});
      setClipsLoaded(true);
    }
  };

  return (
    <div>
      {/* Banner */}
      <div style={{
        background: streamer.has_banner
          ? `url(${process.env.REACT_APP_API_URL || 'http://localhost:8083'}/api/images/banner/${username}) center/cover, linear-gradient(135deg, #1a1a2e, #2a2a3e)`
          : 'linear-gradient(135deg, #1a1a2e, #2a2a3e)',
        padding: '24px 16px 0', borderBottom: '1px solid #2a2a2e'
      }}>
        <div className="profile-banner">
          {/* Avatar */}
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#2a2a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', flexShrink: 0, border: is_live ? '3px solid #eb0400' : '3px solid #2a2a2e' }}>
            {streamer.avatar_url ? <img src={streamer.avatar_url} alt="" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }} /> : '🎥'}
          </div>
          {/* Info */}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 800 }}>{streamer.display_name || username}</h1>
              {is_live && <span className="live-badge" style={{ position: 'static', fontSize: '0.75rem' }}>LIVE</span>}
              <span style={{ fontSize: '0.85rem', color: '#adadb8', textTransform: 'capitalize' }}>📁 {streamer.category}</span>
            </div>
            <div style={{ fontSize: '0.85rem', color: '#adadb8', marginTop: '4px' }}>@{username} · {follower_count} followers</div>
            {streamer.bio && <div style={{ fontSize: '0.9rem', color: '#efeff1', marginTop: '6px', maxWidth: '600px' }}>{streamer.bio}</div>}
          </div>
          {/* Actions */}
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            {is_live && <button className="btn btn-danger" onClick={() => navigate(`/${username}/watch`)}>Watch Live</button>}
            {user?.username !== username && (
              <>
                <button className={`btn ${following ? 'btn-secondary' : 'btn-outline'}`} onClick={handleFollow}>
                  {following ? '✓ Following' : '+ Follow'}
                </button>
                {user && (
                  <button className="btn btn-outline" onClick={() => setShowReport(true)} title="Report" style={{ padding: '8px 10px' }}>
                    ⚑
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {showReport && <ReportDialog username={username} onClose={() => setShowReport(false)} />}

      {/* Main content */}
      <div className="profile-content">
        {/* Left: live player or past streams */}
        <div>
          {is_live && streamer.mux_playback_id && (
            <div style={{ marginBottom: '24px' }}>
              <div className="video-player" style={{ borderRadius: 12 }}>
                <video src={`https://stream.mux.com/${streamer.mux_playback_id}.m3u8`} controls autoPlay muted style={{ width: '100%', height: '100%' }} />
              </div>
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
            {['streams', 'clips'].map(tab => (
              <button key={tab} onClick={() => handleTabChange(tab)}
                style={{ padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem',
                  background: activeTab === tab ? '#D6E9AA' : '#2a2a2e',
                  color: activeTab === tab ? '#fff' : '#adadb8' }}>
                {tab === 'streams' ? '📼 Past Streams' : '✂️ Clips'}
              </button>
            ))}
          </div>

          {/* Past streams / VODs */}
          {activeTab === 'streams' && past_streams.length > 0 && (
            <>
              <div className="section-title" style={{ display: 'none' }}>📼 Past Streams</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px' }}>
                {past_streams.map(s => (
                  <div
                    key={s.id}
                    style={{ background: '#18181b', borderRadius: '10px', overflow: 'hidden', cursor: s.vod_playback_id ? 'pointer' : 'default', transition: 'transform 0.15s' }}
                    onClick={() => s.vod_playback_id && navigate(`/${username}/videos/${s.id}`)}
                    onMouseEnter={e => { if (s.vod_playback_id) e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
                  >
                    <div style={{ position: 'relative', aspectRatio: '16/9', background: '#2a2a2e', overflow: 'hidden' }}>
                      {s.vod_playback_id ? (
                        <img
                          src={`https://image.mux.com/${s.vod_playback_id}/thumbnail.png?time=5&width=400`}
                          alt={s.title}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem' }}>📺</div>
                      )}
                      {s.vod_duration != null && (
                        <span style={{ position: 'absolute', bottom: 6, right: 6, background: 'rgba(0,0,0,0.8)', color: '#fff', fontSize: '0.7rem', fontWeight: 700, padding: '2px 5px', borderRadius: 4 }}>
                          {formatDuration(s.vod_duration)}
                        </span>
                      )}
                      {!s.vod_playback_id && (
                        <span style={{ position: 'absolute', bottom: 6, left: 6, background: 'rgba(0,0,0,0.7)', color: '#adadb8', fontSize: '0.7rem', padding: '2px 5px', borderRadius: 4 }}>Processing...</span>
                      )}
                    </div>
                    <div style={{ padding: '10px' }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</div>
                      <div style={{ fontSize: '0.75rem', color: '#adadb8', textTransform: 'capitalize' }}>{s.category} · {s.started_at ? new Date(s.started_at).toLocaleDateString() : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {activeTab === 'streams' && !is_live && past_streams.length === 0 && (
            <div className="offline-banner">
              <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📺</div>
              <h2>{streamer.display_name || username} is offline</h2>
              <p>Follow to get notified when they go live</p>
            </div>
          )}

          {/* Clips tab */}
          {activeTab === 'clips' && (
            clips.length === 0 ? (
              <div className="offline-banner">
                <div style={{ fontSize: '3rem', marginBottom: '12px' }}>✂️</div>
                <h2>No clips yet</h2>
                <p>Watch a VOD and click ✂️ Clip to create one</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px' }}>
                {clips.map(c => (
                  <div key={c.id} style={{ background: '#18181b', borderRadius: '10px', overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.15s' }}
                    onClick={() => navigate(`/clips/${c.id}`)}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}>
                    <div style={{ position: 'relative', aspectRatio: '16/9', background: '#2a2a2e', overflow: 'hidden' }}>
                      <img src={`https://image.mux.com/${c.mux_playback_id}/thumbnail.png?width=400`} alt={c.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={e => { e.target.style.display = 'none'; }} />
                      {c.duration && (
                        <span style={{ position: 'absolute', bottom: 6, right: 6, background: 'rgba(0,0,0,0.8)', color: '#fff', fontSize: '0.7rem', fontWeight: 700, padding: '2px 5px', borderRadius: 4 }}>
                          {Math.floor(c.duration)}s
                        </span>
                      )}
                      <span style={{ position: 'absolute', top: 6, left: 6, background: '#D6E9AA', color: '#fff', fontSize: '0.65rem', fontWeight: 800, padding: '2px 6px', borderRadius: 4 }}>CLIP</span>
                    </div>
                    <div style={{ padding: '10px' }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</div>
                      <div style={{ fontSize: '0.75rem', color: '#adadb8' }}>
                        👁 {c.view_count} · {new Date(c.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
          {/* Channel Panels */}
          {panels.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <div className="section-title">About</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px' }}>
                {panels.map(p => (
                  <div key={p.id} style={{ padding: '16px', background: '#18181b', borderRadius: '2px', border: '1px solid #2a2a2e', boxShadow: '2px 2px 0 rgba(0,0,0,0.3)' }}>
                    {p.image_url && <img src={p.image_url} alt="" style={{ width: '100%', borderRadius: '2px', marginBottom: '10px' }} onError={e => { e.target.style.display = 'none'; }} />}
                    <div style={{ fontWeight: 800, fontSize: '0.9rem', marginBottom: '6px' }}>{p.title}</div>
                    {p.content && <div style={{ fontSize: '0.82rem', color: '#adadb8', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{p.content}</div>}
                    {p.link_url && <a href={p.link_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.8rem', color: 'var(--accent)', marginTop: '6px', display: 'inline-block' }}>{p.link_url}</a>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: chat (if live) + tip + subscribe */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {is_live && <Chat channel={username} />}

          {/* Subscribe */}
          <div className="panel-card">
            <div className="panel-title">Subscribe</div>
            <p style={{ fontSize: '0.85rem', color: '#adadb8', marginBottom: '12px' }}>
              Support {streamer.display_name || username} with $4.99/month.<br />
              <strong style={{ color: '#D6E9AA' }}>90%</strong> goes directly to them.
            </p>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleSubscribe} disabled={subscribing}>
              {subscribing ? 'Processing...' : '⭐ Subscribe $4.99/mo'}
            </button>
            {subStatus && <p style={{ fontSize: '0.85rem', marginTop: '8px' }}>{subStatus}</p>}
          </div>

          {/* Tip */}
          <div className="panel-card">
            <div className="panel-title">Send a Tip</div>
            <div className="tip-amounts">
              {TIP_PRESETS.map(p => (
                <button key={p} className={`tip-amount-btn ${tipAmount === p ? 'selected' : ''}`} onClick={() => setTipAmount(p)}>
                  ${(p / 100).toFixed(0)}
                </button>
              ))}
            </div>
            <input className="input" type="number" min="1" placeholder="Custom amount ($)"
              value={tipAmount / 100} onChange={e => setTipAmount(Math.round(parseFloat(e.target.value || 0) * 100))} />
            <input className="input" placeholder="Your name (optional)" value={tipName} onChange={e => setTipName(e.target.value)} />
            <input className="input" placeholder="Message (optional)" value={tipMsg} onChange={e => setTipMsg(e.target.value)} />
            <p style={{ fontSize: '0.75rem', color: '#adadb8', marginBottom: '8px' }}>💜 90% goes to streamer · 5% to charity</p>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleTip} disabled={tipping || tipAmount < 100}>
              {tipping ? 'Sending...' : `Tip $${(tipAmount / 100).toFixed(2)}`}
            </button>
            {tipStatus && <p style={{ fontSize: '0.85rem', marginTop: '8px' }}>{tipStatus}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
